'use client'

import { useState, useEffect, useRef, Fragment } from 'react'
import NavLink from '@/components/ui/NavLink'
import { Placeholder } from '@/components/ui/Placeholder'
import ScrollView from '@/components/ui/ScrollView'
import AppIcon from '@/components/content/app/AppIcon'
import RichText from '@/components/content/common/RichText'
import { MenuTitle } from '@/components/navigation/SubMenu'
import { Icon } from '@/icons/Icon'
import { Block } from '@/types/app'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { createLogger } from '@/lib/logger'
import { queryPayload } from '@/lib/payloadClient'
import { getBrandColors, cn, isPopulatedObject } from '@/lib/utils'
import { useHeaderContext } from '@/context/HeaderContext'
import { useComparisonsMenuContext } from '@/context/NavbarContext'
import { orderAppsByRecommendation } from '@/components/content/best/helpers'
import type { App, Category, Comparison, ComparisonAppsApp, Media } from '@/payload-types'

const logger = createLogger('ComparisonsMenu')

type ComparisonsMenuProps = {
  filter?: boolean
}

export default function ComparisonsMenu({ filter }: ComparisonsMenuProps) {
  const { stage, setStage, selectedCategory } = useHeaderContext()

  useEffect(() => {
    if (filter) {
      setStage(2)
    }
  }, [filter, setStage])

  return (
    <div
      data-menu="Comparisons"
      className={cn('overflow-hidden', filter ? 'w-[399px]' : 'w-[800px]')}
    >
      <div
        className={cn(
          'flex shrink-0 transition-transform duration-300 ease-in-out',
          stage === 2
            ? !filter
              ? '-translate-x-1/3'
              : selectedCategory
                ? '-translate-x-1/2'
                : 'translate-x-0'
            : 'translate-x-0',
          filter ? 'w-[800px]' : 'w-[1200px]',
        )}
      >
        {!filter && <ComparisonList />}
        <CategoryList filter={filter} />
        <AppList filter={filter} />
      </div>
    </div>
  )
}

export function ComparisonList() {
  const { comparisons } = useComparisonsMenuContext()

  return (
    <div className="flex w-full flex-col gap-2 px-2 pt-5 navigation:w-1/3 navigation:pb-5">
      <MenuTitle title="Popular Comparisons" />
      <div className="flex flex-col gap-2.5 py-2.5 navigation:gap-5 navigation:py-0">
        {comparisons.length > 0
          ? comparisons.map((comparison) => (
              <ComparisonPreview key={comparison.id} comparison={comparison} />
            ))
          : Array.from({ length: 3 }, (_, index) => (
              <Placeholder className="h-[84px]" key={index} />
            ))}
      </div>
    </div>
  )
}

export function ComparisonPreview({ comparison }: { comparison: Comparison }) {
  const comparisonApps = comparison.comparisonApps?.docs as ComparisonAppsApp[]
  const apps = comparisonApps
    ?.map((item) => {
      if (typeof item.app === 'object') {
        return item.app as App
      }
      return undefined
    })
    .filter((app): app is App => app !== undefined)

  const link = `/compare/${apps
    .map((app) => app.slug)
    .filter(Boolean)
    .join('-vs-')}`

  return (
    <NavLink
      href={link}
      onClick={() => {
        for (const app of apps) {
          trackFeaturedClick(FeaturedPlacement.COMPARE_NAV, app.name)
        }
      }}
      className="group flex items-center gap-5 rounded-xl px-4 py-1.5 transition-colors duration-300 hover:bg-gray-100 dark:hover:bg-white/10 navigation:py-3"
    >
      <div className="flex items-center gap-3">
        {apps.map((app, index) => {
          const appIcon = app?.icon as Media
          const appGlyph = app?.glyph as Media
          const hasIcon = isPopulatedObject<Media>(appIcon) || isPopulatedObject<Media>(appGlyph)
          const { backgroundColor } = getBrandColors(app)

          return (
            <Fragment key={index}>
              {hasIcon && (
                <AppIcon
                  icon={appIcon}
                  glyph={appGlyph}
                  name={app.name}
                  background={backgroundColor}
                  size={40}
                />
              )}
              {index < apps.length - 1 && (
                <span className="text-10 font-medium uppercase text-gray-500">vs</span>
              )}
            </Fragment>
          )
        })}
      </div>

      <div className="flex w-full flex-col gap-2 pt-0.5">
        <div className="flex items-center gap-2">
          <span className="truncate whitespace-nowrap text-14 font-medium leading-none text-gray-950 transition-colors duration-300 group-hover:text-primary-500 dark:text-white">
            {comparison.name}
          </span>
        </div>

        <div className="w-full text-12 text-zinc-500">
          <RichText
            content={comparison?.tldr?.root?.children as Block[]}
            truncate={true}
            maxLength={85}
          />
        </div>
      </div>
    </NavLink>
  )
}

// Category List

export function CategoryList({
  delay = false,
  filter = false,
}: {
  delay?: boolean
  filter?: boolean
}) {
  const { categories } = useComparisonsMenuContext()

  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const { changeSelected, stage, selectedCategory, resetStage } = useHeaderContext()

  const categoryRef = useRef<HTMLDivElement[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current && categories.length > 0 && stage === 1) {
      const timeoutId = window.setTimeout(
        () => {
          inputRef.current?.focus()
        },
        delay ? 500 : 0,
      )
      return () => window.clearTimeout(timeoutId)
    }
  }, [categories, stage, delay])

  const filteredCategories = categories
    .filter((category) => category.name.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => {
      const aFeatured = a.featuredOrder !== null && a.featuredOrder !== undefined
      const bFeatured = b.featuredOrder !== null && b.featuredOrder !== undefined
      if (aFeatured && !bFeatured) return -1
      if (!aFeatured && bFeatured) return 1
      if (aFeatured && bFeatured) return (a.featuredOrder ?? 0) - (b.featuredOrder ?? 0)
      return a.name.localeCompare(b.name)
    })

  const totalItems = filteredCategories.length

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        setSelectedIndex((prev) => Math.min(prev + 1, totalItems - 1))
      } else if (e.key === 'ArrowUp') {
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        if (selectedIndex >= 0 && selectedIndex < filteredCategories.length) {
          changeSelected(filteredCategories[selectedIndex].id)
        }
      } else if (e.key === 'Escape') {
        inputRef.current?.focus()
        resetStage()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedIndex, totalItems, categories, filteredCategories, changeSelected, resetStage])

  useEffect(() => {
    if (selectedIndex > 0) {
      categoryRef.current[selectedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [selectedIndex])

  useEffect(() => {
    if (query !== '') {
      setSelectedIndex(0)
    } else {
      setSelectedIndex(-1)
    }
  }, [query])

  return (
    <>
      <StageHeader heading="Select category" />
      <div
        className={cn(
          'flex w-full flex-col gap-2 border-gray-200 px-2 pt-3 dark:border-white/10 navigation:pt-5',
          filter ? 'navigation:w-1/2' : 'navigation:w-1/3',
          stage === 0 ? 'border-l' : 'border-r',
        )}
      >
        <MenuTitle title="Compare Apps" className="hidden navigation:flex" back={!filter} />

        <div className="flex h-9 w-full items-center gap-4.5 px-3 text-gray-500 navigation:px-5">
          <Icon name="magnifier" />
          <input
            ref={inputRef}
            type="text"
            className="h-full w-full text-16 text-gray-950 placeholder:text-gray-400 focus:outline-none dark:bg-neutral-950 dark:text-white md:text-13"
            placeholder="Filter..."
            aria-label="Filter comparisons"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <ScrollView
          className="flex h-full flex-col gap-1.5 pb-5 navigation:h-[380px]"
          direction="vertical"
        >
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category, index) => {
              return (
                <div
                  key={category.id}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onMouseLeave={() => setSelectedIndex(-1)}
                  onClick={() => changeSelected(category.id)}
                  ref={(el) => {
                    categoryRef.current[index] = el as HTMLDivElement
                  }}
                >
                  <CategorySelect
                    category={category}
                    highlighted={selectedIndex === index}
                    active={selectedCategory === category.id}
                  />
                </div>
              )
            })
          ) : (
            <span className="px-5 text-13 text-gray-500">No categories found</span>
          )}
        </ScrollView>
      </div>
    </>
  )
}

export function CategorySelect({
  category,
  highlighted,
  active,
}: {
  category: Category
  highlighted: boolean
  active: boolean
}) {
  return (
    <div
      className={cn(
        'group flex h-9 shrink-0 cursor-pointer items-center gap-4 rounded-xl pl-3 pr-2 transition-colors navigation:pl-5 navigation:pr-4',
        (highlighted || active) && 'navigation:bg-gray-100 dark:navigation:bg-white/10',
      )}
    >
      <div
        className={cn(
          'flex h-4.5 w-4.5 shrink-0 items-center justify-center text-gray-500 transition-colors',
          active && 'navigation:text-gray-950 dark:navigation:text-white',
        )}
      >
        {category.icon && <Icon name={category.icon} />}
      </div>
      <span
        className={cn(
          'w-full truncate text-13 font-medium text-gray-950 transition-colors dark:text-white',
          active && 'navigation:text-gray-950 dark:navigation:text-white',
        )}
      >
        {category.name}
      </span>
      <Icon
        name="caret-right"
        size={9}
        className={cn('text-gray-400 transition-colors', active && 'navigation:text-primary-500')}
      />
    </div>
  )
}

export function AppList({ filter = false }: { filter?: boolean }) {
  const { selectedCategory, selectedApps } = useHeaderContext()

  const [apps, setApps] = useState<App[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let isMounted = true

    async function fetchAndSetApps() {
      if (!selectedCategory) return

      try {
        if (isMounted) {
          setLoading(true)
        }

        const { docs: acrDocs } = await queryPayload({
          collection: 'app_category_recommendation',
          select: {
            app: true,
            recommendation: true,
            classification: true,
            discontinuedAt: true,
          },
          populate: {
            app: {
              slug: true,
              name: true,
              icon: true,
              glyph: true,
              brandBackgroundColor: true,
              brandPrimaryColor: true,
              categoryRecommendations: true,
            },
            app_category_recommendation: {
              recommendation: true,
            },
            media: {
              url: true,
              alt: true,
              filename: true,
              coveragePercent: true,
              xOffsetPercent: true,
              yOffsetPercent: true,
            },
          },
          where: {
            category: { equals: selectedCategory },
            discontinuedAt: { exists: false },
            'app.review._status': { equals: 'published' },
          },
          pagination: false,
          depth: 2,
          cacheKey: `query:acr:menu:${selectedCategory}`,
        })

        if (acrDocs && isMounted) {
          const { orderedApps } = orderAppsByRecommendation(acrDocs)
          setApps(orderedApps)
        }
      } catch (error) {
        if (isMounted) {
          logger.error('Error fetching apps:', error)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchAndSetApps()

    return () => {
      isMounted = false
    }
  }, [selectedCategory])

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col',
        filter ? 'navigation:w-1/2' : 'navigation:w-1/3',
      )}
    >
      <StageHeader heading="Select apps" />
      <div className="flex h-[calc(100%-50px)] w-full flex-1 flex-col gap-2 px-2 pb-2 pt-3 navigation:pt-5">
        <MenuTitle title="Select" className="hidden navigation:flex" back={filter} />
        <ScrollView
          className="flex h-full flex-col gap-1.5 pb-1 navigation:h-[367px] navigation:pb-0"
          direction="vertical"
        >
          {apps.length > 0
            ? apps.map((app, index) => <AppSelect key={index} app={app} />)
            : Array.from({ length: 9 }, (_, index) => (
                <Placeholder key={index} className="h-10 shrink-0" />
              ))}
        </ScrollView>
        <NavLink
          href={`/compare/${selectedApps
            .map((app) => app.slug)
            .filter(Boolean)
            .join('-vs-')}`}
          className={cn(
            'flex h-10 w-full shrink-0 items-center justify-center whitespace-nowrap rounded-lg text-14 font-medium text-white transition-colors duration-300 ease-in-out',
            selectedApps.length > 1
              ? 'bg-primary-500'
              : 'pointer-events-none bg-gray-300 dark:bg-white/5',
          )}
        >
          {selectedApps.length > 1
            ? `Compare ${selectedApps.length} apps`
            : `Select 2 or more apps`}
        </NavLink>
      </div>
    </div>
  )
}

export function AppSelect({ app }: { app: App }) {
  const { selectedApps, setSelectedApps } = useHeaderContext()
  const { name } = app
  const active = selectedApps.includes(app)
  const maxLimit = selectedApps.length === 4

  const handleToggleCompare = () => {
    setSelectedApps((prev: App[]) => {
      if (prev.includes(app)) {
        return prev.filter((item) => item !== app)
      } else {
        return [...prev, app]
      }
    })
  }

  const appIcon = app?.icon as Media
  const appGlyph = app?.glyph as Media
  const hasIcon = isPopulatedObject<Media>(appIcon) || isPopulatedObject<Media>(appGlyph)
  const { backgroundColor } = getBrandColors(app)

  return (
    <div
      onClick={maxLimit && !active ? undefined : handleToggleCompare}
      className={cn(
        'group flex h-9 shrink-0 cursor-pointer items-center gap-3.5 rounded-xl px-3 transition-all hover:bg-gray-100 dark:hover:bg-white/10 navigation:px-5',
        maxLimit && !active && 'pointer-events-none opacity-50',
      )}
    >
      <div
        className={cn(
          'flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-[5px] border border-black/10 bg-white text-white transition-colors duration-300 ease-in-out dark:border-white/10 dark:bg-neutral-950',
          active && 'bg-primary-500 dark:bg-primary-500',
        )}
      >
        <Icon
          name="check"
          className={cn(
            'scale-0 opacity-0 transition-all duration-300 ease-in-out',
            active && 'scale-75 opacity-100',
          )}
        />
      </div>

      {hasIcon && (
        <AppIcon
          icon={appIcon}
          glyph={appGlyph}
          name={app.name}
          background={backgroundColor}
          size={24}
        />
      )}

      <span className="truncate text-13 font-medium text-gray-950 transition-colors dark:text-white">
        {name}
      </span>
    </div>
  )
}

export function CreateComparison() {
  const { setStage } = useHeaderContext()

  const changeStage = (stage: number) => {
    setStage(stage)
  }

  return (
    <div className="flex flex-col gap-2">
      <MenuTitle title="Compare Apps" />
      <div
        onClick={() => changeStage(1)}
        className="group mx-6 mb-5 flex h-10 w-[calc(100%-3rem)] cursor-pointer items-center gap-3.5 rounded-lg border border-gray-200 bg-gray-50 px-3 transition-colors hover:border-gray-300 dark:border-white/10 dark:bg-neutral-950 dark:hover:border-white/20 navigation:hidden [&>svg]:text-gray-400 dark:[&>svg]:text-white/20"
      >
        <Icon name="add-item" />
        <span className="w-full text-gray-800 dark:text-white/80">Create Comparison</span>
        <Icon name="arrow-left" className="rotate-180" />
      </div>
    </div>
  )
}

export function StageHeader({ heading }: { heading: string }) {
  const { stage, setStage, setSelectedCategory, setSelectedApps } = useHeaderContext()

  const resetSelections = () => {
    setSelectedCategory(null)
    setSelectedApps([])
  }

  const handleBack = () => {
    if (stage === 1) {
      setStage(0)
    } else if (stage === 2) {
      setStage(1)
    }

    resetSelections()
  }

  return (
    <div
      onClick={handleBack}
      className="sticky top-0 z-30 flex h-[50px] shrink-0 cursor-pointer items-center border-b border-gray-200 bg-white/90 pl-1 pr-5 text-13 text-gray-500 backdrop-blur-md dark:border-white/10 dark:bg-black/90 navigation:hidden"
    >
      <div className="flex aspect-square h-full cursor-pointer items-center justify-center">
        <Icon name="arrow-left" />
      </div>
      <span>{heading}</span>
    </div>
  )
}
